import { UserId } from "@inrev/common";
import { useAdminFetchAdminUsers } from "@portal/api/admin-user/admin";
import {
	useAdminFetchAgencyPreviews,
	useAdminFetchAgentPreviewsForAgency,
} from "@portal/api/agency/admin";
import { useRequireActiveAgentFollower } from "@portal/api/followers";
import { FormItemLabel, FormSection } from "@portal/components/form";
import { FormDropdownComponent } from "@portal/components/form/fieldLayouts";
import { FormSearchableInputWithDropdown } from "@portal/components/form/fields";
import { DropdownInput } from "@portal/components/form/inputs/DropdownInput";
import { cn } from "@portal/components/lib/utils";
import { Icon } from "@portal/components/primitives";
import { CenteredSpinner } from "@portal/components/ui/CenteredSpinner";
import { Spinner } from "@portal/components/ui/Spinner";
import { useAuthenticatedUser } from "@portal/providers/AuthenticatedUserProvider";
import { useCallback, useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { Tooltip } from "react-tooltip";
import { assigneeUserFromQuery } from "../../../../shared/AdminAssigneeControls";
import { AdminFormFollowers } from "../../../../shared/submission/AdminFormFollowers";
import { ActionItemSelectAssignee } from "../../../../shared/tools/actionItems/components/ActionItemSelectAssignee";
import type { AdminCombinedFormData } from "../../schema";

type PrincipalFormData = Pick<
	AdminCombinedFormData,
	"agencyId" | "agentId" | "underwriterId" | "followers"
>;

export const PrincipalStep = () => {
	const formMethods = useFormContext<PrincipalFormData>();
	const { control, watch, setValue, trigger, formState, setError, clearErrors } = formMethods;
	const { user } = useAuthenticatedUser();
	const { adminUsers = [] } = useAdminFetchAdminUsers();
	const { agencyPreviews, agencyPreviewsLoading } = useAdminFetchAgencyPreviews();

	const selectedAgencyId = watch("agencyId");
	const selectedAgentId = watch("agentId");
	const followers = watch("followers");
	const followerErrors = formState.errors.followers;

	const { agentPreviews: agencyAgents, agentPreviewsLoading } = useAdminFetchAgentPreviewsForAgency(
		selectedAgencyId,
		true,
	);
	const agentPreviews = agencyAgents?.filter((agent) => !agent.invitationPending);

	const defaultUnderwriterId = useMemo(() => {
		if (selectedAgencyId && agencyPreviews) {
			return agencyPreviews.find((agency) => agency.id === selectedAgencyId)?.underwriterUserId;
		}
	}, [selectedAgencyId, agencyPreviews]);

	useEffect(() => {
		if (defaultUnderwriterId) {
			setValue("underwriterId", defaultUnderwriterId);
		}
	}, [defaultUnderwriterId]);

	useEffect(() => {
		if (!followers?.length && selectedAgentId) {
			setValue("followers", [selectedAgentId]);
			if (formState.errors.followers) {
				trigger("followers");
			}
		}
	}, [selectedAgentId]);

	useRequireActiveAgentFollower(followers, agencyAgents, {
		setError: (message) => setError("followers", { type: "manual", message }),
		clearError: () => clearErrors("followers"),
		hasManualError: formState.errors.followers?.type === "manual",
	});

	const onFollow = useCallback(
		(id: UserId) => {
			setValue("followers", [...(followers || []), id]);
			if (formState.errors.followers) {
				trigger("followers");
			}
		},
		[followers, setValue, formState.errors.followers, trigger],
	);

	const onUnfollow = useCallback(
		(id: UserId) => {
			setValue(
				"followers",
				(followers || []).filter((follower) => follower !== id),
			);
		},
		[followers, setValue],
	);

	if (!agencyPreviews || agencyPreviewsLoading) {
		return <Spinner />;
	}

	return (
		<div className="h-full w-full flex flex-col space-y-6 pb-[125px]">
			<FormSection topPadding header="Agency Details" id="agency">
				<div className="flex flex-col space-y-[18px]">
					<FormItemLabel name="agencyId">Agency</FormItemLabel>
					{agencyPreviewsLoading || !agencyPreviews ? (
						<CenteredSpinner />
					) : (
						<FormSearchableInputWithDropdown
							control={control}
							name="agencyId"
							placeholder="Select Agency"
							options={agencyPreviews.map((agency) => ({
								value: agency.id,
								label: agency.displayName,
							}))}
							compact
						/>
					)}
					<>
						{agentPreviews ? (
							<FormDropdownComponent
								formMethods={formMethods}
								name="agentId"
								label="Agent"
								placeholder="Select Agent"
								options={agentPreviews.map((agent) => ({
									value: agent.id,
									label: `${agent.firstName} ${agent.lastName}`,
								}))}
								optionsClassName="max-h-[400px]"
								compact
							/>
						) : (
							<>
								<FormItemLabel>Agent</FormItemLabel>
								<Tooltip
									offset={24}
									id="agent-tooltip"
									content="Select an agency before setting an agent"
								/>
								<DropdownInput
									data-tooltip-id="agent-tooltip"
									options={[]}
									placeholder={agentPreviewsLoading ? "Loading..." : "Select an agency"}
									disabled
									compact
								/>
							</>
						)}
						<div className="flex flex-row justify-start space-x-10 items-center">
							<div className="flex flex-row gap-2 items-center">
								<FormItemLabel name="underwriterId">Underwriter</FormItemLabel>
								{defaultUnderwriterId && selectedAgencyId ? (
									<ActionItemSelectAssignee
										defaultUnderwriterId={user.id}
										userList={adminUsers.map(assigneeUserFromQuery)}
										selectedUserId={watch("underwriterId")}
										setUserId={(userId: UserId) => setValue("underwriterId", userId)}
									/>
								) : (
									<div className="flex items-center justify-center w-[28px] h-[28px] text-[12px] font-medium text-secondary border border-bc-strong bg-surface-neutral hover:text-primary hover:border-bc-stronger rounded-full opacity-50">
										??
									</div>
								)}
							</div>
							<div className="flex flex-row gap-2 items-center">
								<FormItemLabel name="followers">Followers</FormItemLabel>
								{agencyAgents ? (
									<AdminFormFollowers
										followers={followers}
										agentPreviews={agencyAgents}
										onFollow={onFollow}
										onUnfollow={onUnfollow}
										error={followerErrors}
									/>
								) : (
									<>
										<Tooltip
											id="followers-tooltip"
											content="Select an agency before setting followers"
										/>
										<div className="relative">
											<div
												data-tooltip-id="followers-tooltip"
												className={cn(
													"flex items-center justify-center w-fit h-[24px] min-h-[24px] space-x-[5.5px] px-[10px] text-[12.5px] font-[550] text-secondary border border-bc-strong bg-surface-base rounded-full opacity-50",
													followerErrors ? "border-danger" : "",
												)}
											>
												<span>{followers?.length || 0}</span>
												<Icon type="users" size="micro" color="secondary" />
											</div>
											{followerErrors && followerErrors.message && (
												<span className="absolute text-danger text-[11px] font-normal -top-4 left-0 text-nowrap">
													{followerErrors.message}
												</span>
											)}
										</div>
									</>
								)}
							</div>
						</div>
					</>
				</div>
			</FormSection>
		</div>
	);
};
